import { Link } from "react-router-dom";
import { Clock, ListChecks } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

const CATEGORY_LABELS = {
  personal_care: "Personal Care",
  medication: "Medication",
  mobility: "Mobility",
  companionship: "Companionship",
  nursing: "Nursing",
  custom: "Custom",
};

const CATEGORY_TONES = {
  personal_care: "border-cyan-200 bg-cyan-500/10 text-cyan-700 dark:border-cyan-800 dark:text-cyan-300",
  medication: "border-amber-200 bg-amber-500/10 text-amber-700 dark:border-amber-800 dark:text-amber-300",
  mobility: "border-indigo-200 bg-indigo-500/10 text-indigo-700 dark:border-indigo-800 dark:text-indigo-300",
  companionship: "border-rose-200 bg-rose-500/10 text-rose-700 dark:border-rose-800 dark:text-rose-300",
  nursing: "border-emerald-200 bg-emerald-500/10 text-emerald-700 dark:border-emerald-800 dark:text-emerald-300",
  custom: "border-slate-200 bg-slate-500/10 text-slate-700 dark:border-slate-700 dark:text-slate-300",
};

export function TemplateCard({ template, onAssign }) {
  const taskCount = template.tasks_count ?? template.tasks?.length ?? 0;
  const categoryKey = template.category;
  const categoryTone = CATEGORY_TONES[categoryKey] || CATEGORY_TONES.custom;

  return (
    <Card className="flex flex-col">
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0 pb-2">
        <CardTitle className="text-base leading-snug">{template.name}</CardTitle>
        <Badge variant={template.is_active ? "default" : "secondary"}>
          {template.is_active ? "Active" : "Archived"}
        </Badge>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col gap-3">
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline" className={cn("font-normal", categoryTone)}>
            {CATEGORY_LABELS[categoryKey] ||
              template.category_display ||
              template.category}
          </Badge>
          <Badge variant="secondary" className="gap-1 font-normal">
            <Clock className="size-3 text-cyan-600" />
            {template.default_duration_minutes} mins
          </Badge>
          <Badge variant="secondary" className="gap-1 font-normal">
            <ListChecks className="size-3 text-amber-600" />
            {taskCount} tasks
          </Badge>
        </div>

        {template.description ? (
          <p className="line-clamp-2 text-sm text-muted-foreground">{template.description}</p>
        ) : null}
      </CardContent>

      <CardFooter className="gap-2 pt-0">
        <Button asChild variant="outline" className="flex-1">
          <Link to={`/care-plans/templates/${template.id}`}>Edit Template</Link>
        </Button>
        {onAssign ? (
          <Button type="button" onClick={() => onAssign(template)}>
            Assign
          </Button>
        ) : null}
      </CardFooter>
    </Card>
  );
}
